import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Zap, AlertTriangle } from "lucide-react";
import { apiPost } from "../utils/api";
import { storeToken, storeGameSession } from "../utils/auth";
import { useAuth } from "../hooks/useAuth.jsx";

const EMAIL_REGEX =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
const USERNAME_REGEX = /^[A-Za-z0-9_]+$/;

const validateLoginUsername = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: "Username or email is required" };
  }
  if (trimmed.includes("@") && !EMAIL_REGEX.test(trimmed)) {
    return { valid: false, message: "Enter a valid email address" };
  }
  return { valid: true, message: "" };
};

const validateLoginPassword = (value) => {
  if (!value) {
    return { valid: false, message: "Password is required" };
  }
  return { valid: true, message: "" };
};

const validateRegisterUsername = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: "Username is required" };
  }
  if (trimmed.length < 3 || trimmed.length > 30) {
    return {
      valid: false,
      message: "Username must be 3–30 characters long",
    };
  }
  if (!USERNAME_REGEX.test(trimmed)) {
    return {
      valid: false,
      message: "Use only letters, numbers, or underscores",
    };
  }
  return { valid: true, message: "" };
};

const validateEmail = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: "Email is required" };
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    return { valid: false, message: "Enter a valid email address" };
  }
  return { valid: true, message: "" };
};

const validateRegisterPassword = (value) => {
  if (!value) {
    return { valid: false, message: "Password is required" };
  }
  if (value.length < 8) {
    return {
      valid: false,
      message: "Password must be at least 8 characters long",
    };
  }
  return { valid: true, message: "" };
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();
  const appLoginUrl = `${import.meta.env.VITE_APP_URL || window.location.origin}/login`;
  const [mode, setMode] = useState(location.state?.authMode || "login");
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [error, setError] = useState("");
  const [dirtyFields, setDirtyFields] = useState({
    username: false,
    email: false,
    password: false,
  });
  const verificationTokenInFlight = useRef("");

  const loginUsernameValidation = validateLoginUsername(form.username);
  const loginPasswordValidation = validateLoginPassword(form.password);
  const registerUsernameValidation = validateRegisterUsername(form.username);
  const registerEmailValidation = validateEmail(form.email);
  const registerPasswordValidation = validateRegisterPassword(form.password);
  const forgotEmailValidation = validateEmail(form.email);

  const isLoginFormValid =
    loginUsernameValidation.valid && loginPasswordValidation.valid;
  const isRegisterFormValid =
    registerUsernameValidation.valid &&
    registerEmailValidation.valid &&
    registerPasswordValidation.valid;
  const isForgotFormValid = forgotEmailValidation.valid;

  const markFieldDirty = (field) => {
    setDirtyFields((prev) =>
      prev[field] ? prev : { ...prev, [field]: true },
    );
  };

  const resetDirtyFields = () => {
    setDirtyFields({ username: false, email: false, password: false });
  };

  const fieldBorderClass = (isInvalid) =>
    isInvalid
      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
      : "border-gray-600 focus:border-blue-500 focus:ring-blue-500/20";

  const fromLocation = location.state?.from;
  const from = fromLocation
    ? `${fromLocation.pathname || "/"}${fromLocation.search || ""}${fromLocation.hash || ""}`
    : "/";

  const handleAuthSuccess = (data) => {
    const userData = {
      username: data.username,
      user_id: data.user_id,
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };

    storeToken(data.access_token, userData);
    login(userData);

    if (data.game_session) {
      storeGameSession(data.game_session);
      navigate("/game", { replace: true });
      return;
    }

    navigate(from, { replace: true });
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError("");
    setInfoMessage("");
    setShowResendVerification(false);
    setResetToken("");
    resetDirtyFields();
    setForm({
      username: form.username,
      email: "",
      password: "",
      confirmPassword: "",
    });
  };

  const readResponseBody = async (res) => {
    const text = await res.text();
    if (!text) {
      return {};
    }
    try {
      return JSON.parse(text);
    } catch {
      return { detail: text };
    }
  };

  const showForgotPassword = () => {
    const prefilledEmail = form.username.includes("@") ? form.username : "";
    setMode("forgot");
    setError("");
    setInfoMessage("");
    setShowResendVerification(false);
    setDirtyFields({
      username: false,
      email: Boolean(prefilledEmail),
      password: false,
    });
    setForm({
      username: "",
      email: prefilledEmail,
      password: "",
      confirmPassword: "",
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verifyToken = params.get("verify_email_token");
    const passwordToken = params.get("reset_password_token");

    if (passwordToken && !isAuthenticated) {
      setResetToken(passwordToken);
      setMode("reset");
      setError("");
      setInfoMessage("");
      window.history.replaceState({}, "", "/login");
      return;
    }

    if (!verifyToken || isAuthenticated) {
      return;
    }

    // Strict Mode runs effects twice in development. A verification token is
    // one-time use, so prevent the duplicate request from replacing success
    // with an "invalid or expired" response.
    if (verificationTokenInFlight.current === verifyToken) {
      return;
    }
    verificationTokenInFlight.current = verifyToken;

    const verifyEmailToken = async () => {
      setVerifyingEmail(true);
      setError("");
      setInfoMessage("");

      try {
        const res = await apiPost("/auth/email-verification/verify", {
          token: verifyToken,
        });
        const data = await readResponseBody(res);

        if (!res.ok) {
          setError(data.detail || "Verification link is invalid or expired");
          setShowResendVerification(true);
          window.history.replaceState({}, "", "/login");
          return;
        }

        setInfoMessage(
          data.message || "Email verified successfully. Please sign in.",
        );
        setShowResendVerification(false);
        window.history.replaceState({}, "", "/login");
      } catch (err) {
        console.error("Email verify error:", err);
        setError(
          "Unable to verify email. Please request a new verification link.",
        );
        setShowResendVerification(true);
        window.history.replaceState({}, "", "/login");
      } finally {
        setVerifyingEmail(false);
      }
    };

    verifyEmailToken();
  }, [location.search, isAuthenticated]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfoMessage("");

    try {
      if (mode === "forgot") {
        const res = await apiPost("/auth/password-reset/request", {
          email: form.email,
          redirect_url: appLoginUrl,
        });
        const data = await readResponseBody(res);
        if (!res.ok) {
          setError(data.detail || "Failed to send reset email");
          return;
        }
        setInfoMessage(
          data.message || "Check your email for a password reset link.",
        );
        return;
      }

      if (mode === "reset") {
        if (form.password !== form.confirmPassword) {
          setError("Passwords do not match");
          return;
        }
        const res = await apiPost("/auth/password-reset/confirm", {
          token: resetToken,
          password: form.password,
        });
        const data = await readResponseBody(res);
        if (!res.ok) {
          setError(data.detail || "Failed to reset password");
          return;
        }
        setInfoMessage(
          data.message || "Password reset successfully. Please sign in.",
        );
        setMode("login");
        setResetToken("");
        resetDirtyFields();
        setForm({ username: "", email: "", password: "", confirmPassword: "" });
        return;
      }

      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { username: form.username, password: form.password }
          : {
              username: form.username,
              email: form.email,
              password: form.password,
              redirect_url: appLoginUrl,
            };
      const res = await apiPost(endpoint, body);
      const data = await readResponseBody(res);

      if (res.ok && mode === "login") {
        setShowResendVerification(false);
        handleAuthSuccess(data);
      } else if (res.ok && mode === "register") {
        setInfoMessage(
          data.message ||
            "Registration successful. Check your inbox and verify your email before signing in.",
        );
        setResendEmail(form.email);
        setShowResendVerification(true);
        setMode("login");
        resetDirtyFields();
        setForm({
          username: form.username,
          email: "",
          password: "",
          confirmPassword: "",
        });
      } else {
        console.error("Error response:", data);
        setError(data.detail || "Authentication failed");
        if (
          mode === "login" &&
          data.detail?.toLowerCase().includes("verify your email")
        ) {
          setShowResendVerification(true);
          if (form.username.includes("@")) {
            setResendEmail(form.username);
          }
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const res = await apiPost("/auth/email-verification/request", {
        email: resendEmail,
        redirect_url: appLoginUrl,
      });

      const data = await readResponseBody(res);

      if (!res.ok) {
        setError(data.detail || "Failed to send verification email");
        return;
      }

      setInfoMessage(
        data.message || "Check your email for a verification link.",
      );
    } catch (err) {
      console.error("Email verification request error:", err);
      setError("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const authBusy = loading || verifyingEmail || resendLoading;
  const authBusyLabel = verifyingEmail
    ? "Verifying your email..."
    : resendLoading
      ? "Sending verification email..."
      : mode === "login"
        ? "Signing you in..."
        : mode === "register"
          ? "Creating your account..."
          : mode === "forgot"
            ? "Sending reset link..."
            : "Updating your password...";

  return (
    <>
      {authBusy && (
        <div className="auth-action-loader fixed inset-0 z-[100] grid place-items-center bg-[#080712]/85 p-6 backdrop-blur-md" role="status" aria-live="polite" aria-label={authBusyLabel}>
          <div className="w-full max-w-xs rounded-2xl border border-purple-400/20 bg-[#110f1e] px-6 py-7 text-center shadow-2xl">
            <div className="app-loader__mark !mb-3">
              <span className="app-loader__ring" aria-hidden="true"><span /><span /></span>
              <img
                src="https://asset.langprotect.com/lp-game/favicon.png"
                onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = '/favicon.png'; }}
                alt=""
                className="app-loader__icon"
              />
            </div>
            <p className="text-sm font-semibold text-gray-100">{authBusyLabel}</p>
            <p className="mt-1 text-xs text-gray-500">Securing your session</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-center min-h-screen p-6">
      <div className="w-full max-w-md bg-black/20 backdrop-blur-2xl rounded-2xl shadow-2xl border border-gray-700/50">
        <div className="p-8 pb-6">
          <div className="flex flex-col items-center mb-8">
            <img
              src="https://asset.langprotect.com/lp-game/favicon.png"
              onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = '/favicon.png'; }}
              alt="AI Security Lab"
              width="56"
              height="56"
              fetchPriority="high"
              decoding="async"
              className="h-14 w-14 mb-4"
            />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              AI Escape Room
            </h1>
            <p className="text-sm text-gray-400">
              {mode === "login" && "Sign in to continue your mission"}
              {mode === "register" &&
                "Create your account and verify your email"}
              {mode === "forgot" && "Send a secure reset link to your email"}
              {mode === "reset" && "Choose a new password"}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <div className="flex items-center space-x-2 text-red-400 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {infoMessage && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
              <p className="text-green-400 text-sm break-all">{infoMessage}</p>
            </div>
          )}

          {verifyingEmail && (
            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-3">
              <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-300/30 border-t-blue-300" />
              <p className="text-blue-300 text-sm">Verifying your email...</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {(mode === "login" || mode === "register") && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">
                  {mode === "login" ? "Username or Email" : "Username"}
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => {
                    markFieldDirty("username");
                    setForm({ ...form, username: e.target.value });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-gray-700/50 text-white focus:bg-gray-700 focus:outline-none focus:ring-2 ${
                    dirtyFields.username &&
                    !(mode === "login"
                      ? loginUsernameValidation.valid
                      : registerUsernameValidation.valid)
                      ? fieldBorderClass(true)
                      : fieldBorderClass(false)
                  }`}
                  placeholder={
                    mode === "login"
                      ? "Enter username or email"
                      : "Enter your username"
                  }
                  required
                />
                {mode === "login" &&
                  dirtyFields.username &&
                  !loginUsernameValidation.valid && (
                    <p className="text-xs text-red-400">
                      {loginUsernameValidation.message}
                    </p>
                  )}
                {mode === "register" &&
                  dirtyFields.username &&
                  !registerUsernameValidation.valid && (
                    <p className="text-xs text-red-400">
                      {registerUsernameValidation.message}
                    </p>
                  )}
                {mode === "register" &&
                  !(dirtyFields.username && !registerUsernameValidation.valid) && (
                  <p className="text-xs text-gray-500">
                    3–30 letters, numbers, or underscores. Usernames are unique.
                  </p>
                )}
              </div>
            )}

            {(mode === "register" || mode === "forgot") && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    markFieldDirty("email");
                    setForm({ ...form, email: e.target.value });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-gray-700/50 text-white focus:bg-gray-700 focus:outline-none focus:ring-2 ${
                    dirtyFields.email &&
                    !(mode === "forgot"
                      ? forgotEmailValidation.valid
                      : registerEmailValidation.valid)
                      ? fieldBorderClass(true)
                      : fieldBorderClass(false)
                  }`}
                  placeholder="Enter your email"
                  required
                />
                {dirtyFields.email &&
                  !(mode === "forgot"
                    ? forgotEmailValidation.valid
                    : registerEmailValidation.valid) && (
                    <p className="text-xs text-red-400">
                      {(mode === "forgot"
                        ? forgotEmailValidation
                        : registerEmailValidation
                      ).message}
                    </p>
                  )}
              </div>
            )}

            {(mode === "login" || mode === "register" || mode === "reset") && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">
                  {mode === "reset" ? "New Password" : "Password"}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => {
                    if (mode === "login" || mode === "register") {
                      markFieldDirty("password");
                    }
                    setForm({ ...form, password: e.target.value });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-gray-700/50 text-white focus:bg-gray-700 focus:outline-none focus:ring-2 ${
                    mode === "login" &&
                    dirtyFields.password &&
                    !loginPasswordValidation.valid
                      ? fieldBorderClass(true)
                      : mode === "register" &&
                          dirtyFields.password &&
                          !registerPasswordValidation.valid
                        ? fieldBorderClass(true)
                        : fieldBorderClass(false)
                  }`}
                  placeholder={
                    mode === "login"
                      ? "Enter your password"
                      : "Enter password (min 8 characters)"
                  }
                  minLength={mode !== "login" ? 8 : undefined}
                  required
                />
                {mode === "register" && (
                  <div className="flex items-center space-x-2 mt-1">
                    <div
                      className={`w-2 h-2 rounded-full ${registerPasswordValidation.valid ? "bg-green-500" : "bg-gray-500"}`}
                    ></div>
                    <p
                      className={`text-xs ${
                        registerPasswordValidation.valid
                          ? "text-green-400"
                          : dirtyFields.password &&
                              !registerPasswordValidation.valid
                            ? "text-red-400"
                            : "text-gray-400"
                      }`}
                    >
                      Password must be at least 8 characters long{" "}
                      {form.password.length > 0 &&
                        `(${form.password.length}/8)`}
                    </p>
                  </div>
                )}
                {mode === "reset" && (
                  <div className="flex items-center space-x-2 mt-1">
                    <div
                      className={`w-2 h-2 rounded-full ${form.password.length >= 8 ? "bg-green-500" : "bg-gray-500"}`}
                    ></div>
                    <p
                      className={`text-xs ${form.password.length >= 8 ? "text-green-400" : form.password.length > 0 && form.password.length < 8 ? "text-red-400" : "text-gray-400"}`}
                    >
                      Password must be at least 8 characters long{" "}
                      {form.password.length > 0 &&
                        `(${form.password.length}/8)`}
                    </p>
                  </div>
                )}
                {mode === "login" &&
                  dirtyFields.password &&
                  !loginPasswordValidation.valid && (
                    <p className="text-xs text-red-400">
                      {loginPasswordValidation.message}
                    </p>
                  )}
                {mode === "login" && (
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={showForgotPassword}
                      className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
            )}

            {mode === "reset" && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) =>
                    setForm({ ...form, confirmPassword: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="Re-enter your password"
                  minLength={8}
                  required
                />
              </div>
            )}

            {mode === "forgot" && (
              <p className="text-sm text-gray-400 leading-relaxed">
                Enter the verified email for your account. If it exists, we will
                send a one-time reset link.
              </p>
            )}

            <button
              type="submit"
              disabled={
                loading ||
                verifyingEmail ||
                (mode === "login" && !isLoginFormValid) ||
                (mode === "register" && !isRegisterFormValid) ||
                (mode === "forgot" && !isForgotFormValid) ||
                (mode === "reset" && form.password.length < 8)
              }
              className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>
                    {mode === "login" && "Signing in..."}
                    {mode === "register" && "Creating account..."}
                    {mode === "forgot" && "Sending reset link..."}
                    {mode === "reset" && "Updating password..."}
                  </span>
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5" />
                  <span>
                    {mode === "login" && "Sign In"}
                    {mode === "register" && "Create Account"}
                    {mode === "forgot" && "Send Reset Link"}
                    {mode === "reset" && "Reset Password"}
                  </span>
                </>
              )}
            </button>
          </form>

          {mode === "login" && showResendVerification && (
            <div className="mt-6 pt-6 border-t border-gray-200/10">
              <p className="text-sm text-gray-300 mb-3">
                Didn't receive verification email?
              </p>
              <form onSubmit={handleResendVerification} className="space-y-3">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border transition-all duration-200 bg-gray-700/50 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="Enter your email"
                  required
                />
                <button
                  type="submit"
                  disabled={resendLoading || verifyingEmail}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {resendLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Resend Verification Email"
                  )}
                </button>
              </form>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200/10">
            <p className="text-center text-sm text-gray-400">
              {mode === "login" && "Don't have an account?"}
              {mode === "register" && "Already have an account?"}
              {(mode === "forgot" || mode === "reset") &&
                "Remembered your password?"}
              <button
                onClick={
                  mode === "login" || mode === "register"
                    ? toggleMode
                    : () => {
                        setMode("login");
                        setError("");
                        setInfoMessage("");
                        setResetToken("");
                        resetDirtyFields();
                        setForm({
                          username: "",
                          email: "",
                          password: "",
                          confirmPassword: "",
                        });
                      }
                }
                className="ml-1 text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                {mode === "login" && "Sign up"}
                {mode !== "login" && "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoginPage;
